import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import AlertBanner from '@/components/common/AlertBanner';
import InputError from '@/components/Forms/InputError';
import SelectInput from '@/components/Forms/SelectInput';
import TextInput from '@/components/Forms/TextInput';
import ThemeWrapper from '@/components/ThemeWrapper';
import useRoutes from '@/hooks/useRoutes';
import StaffLayout from '@/layouts/StaffLayout';
import { Academy } from '@/types';
import { Link, router } from '@inertiajs/react';
import React, { useState } from 'react';

interface CreateProps {
    academies: Academy[];
    allowed_domains: string[];
    errors?: Record<string, string>;
    form_data?: {
        email?: string;
        first_name?: string;
        last_name?: string;
        academy_id?: number;
    };
}

const CreateContent: React.FC<CreateProps> = ({
    academies,
    allowed_domains,
    errors: serverErrors,
    form_data,
}) => {
    const { route } = useRoutes();

    // Parse email if provided
    const emailParts = form_data?.email?.split('@') || ['', ''];
    const initialUsername = emailParts[0] || '';
    const initialDomain = emailParts[1] || allowed_domains[0] || '';

    const [data, setDataState] = useState({
        email_username: initialUsername,
        email_domain: initialDomain,
        first_name: form_data?.first_name || '',
        last_name: form_data?.last_name || '',
        academy_id: form_data?.academy_id || '',
    });
    const [processing, setProcessing] = useState(false);

    const setData = (key: string, value: any) => {
        setDataState((prev) => ({ ...prev, [key]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setProcessing(true);

        router.post(
            route('staff-commercial-users-create').toString(),
            {
                email: `${data.email_username}@${data.email_domain}`,
                first_name: data.first_name,
                last_name: data.last_name,
                academy_id: data.academy_id,
            },
            {
                preserveScroll: true,
                onFinish: () => setProcessing(false),
            }
        );
    };

    const allErrors = serverErrors || {};

    return (
        <StaffLayout>
            <div className="mx-auto max-w-3xl">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Crear Usuario Comercial
                    </h1>
                    <p className="mt-2 text-gray-600 dark:text-neutral-300">
                        Completa el formulario para crear un nuevo usuario
                        comercial
                    </p>
                </div>

                <AlertBanner
                    variant="info"
                    title="Al crear este usuario:"
                    className="mb-6"
                >
                    <ul className="mt-2 ml-4 list-disc space-y-1">
                        <li>
                            Se generará automáticamente una contraseña segura
                        </li>
                        <li>
                            El usuario será añadido a los grupos "Comercial" y
                            "Estudiante"
                        </li>
                        <li>
                            Se inscribirá automáticamente en todos los cursos de
                            la academia seleccionada
                        </li>
                        <li>
                            Se enviará un correo con las credenciales de acceso
                        </li>
                        <li>
                            Deberá cambiar su contraseña en el primer inicio de
                            sesión
                        </li>
                    </ul>
                </AlertBanner>

                <div className="rounded-lg bg-white p-6 shadow dark:bg-neutral-800">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Academy - First Field */}
                        <SelectInput
                            label="Academia"
                            name="academy_id"
                            value={data.academy_id}
                            onChange={(v) => setData('academy_id', Number(v))}
                            error={allErrors.academy_id}
                        >
                            <option value="">Selecciona una academia</option>
                            {academies.map((academy) => (
                                <option key={academy.id} value={academy.id}>
                                    {academy.name}
                                </option>
                            ))}
                        </SelectInput>

                        {/* Email - Split into username and domain */}
                        <div>
                            <label
                                htmlFor="email_username"
                                className="mb-1 block text-sm font-medium text-gray-500 dark:text-neutral-400"
                            >
                                Email
                            </label>
                            <div className="flex gap-2">
                                <TextInput
                                    name="email_username"
                                    id="email_username"
                                    value={data.email_username}
                                    onChange={(v) =>
                                        setData('email_username', v)
                                    }
                                    placeholder="usuario"
                                    className="flex-1"
                                />
                                <span className="flex items-center text-gray-500 dark:text-neutral-400">
                                    @
                                </span>
                                <SelectInput
                                    name="email_domain"
                                    id="email_domain"
                                    value={data.email_domain}
                                    onChange={(v) => setData('email_domain', v)}
                                    className="flex-1"
                                >
                                    {allowed_domains.map((domain) => (
                                        <option key={domain} value={domain}>
                                            {domain}
                                        </option>
                                    ))}
                                </SelectInput>
                            </div>
                            <InputError error={allErrors.email} />
                            <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400">
                                Dominios permitidos:{' '}
                                {allowed_domains.join(', ')}
                            </p>
                        </div>

                        {/* First Name */}
                        <TextInput
                            label="Nombre"
                            name="first_name"
                            value={data.first_name}
                            onChange={(v) => setData('first_name', v)}
                            error={allErrors.first_name}
                        />

                        {/* Last Name */}
                        <TextInput
                            label="Apellido"
                            name="last_name"
                            value={data.last_name}
                            onChange={(v) => setData('last_name', v)}
                            error={allErrors.last_name}
                        />

                        {allErrors.general && (
                            <AlertBanner variant="error">
                                {allErrors.general}
                            </AlertBanner>
                        )}

                        {/* Actions */}
                        <div className="flex justify-end space-x-3 pt-4">
                            <Link
                                href={route(
                                    'staff-commercial-users'
                                ).toString()}
                            >
                                <SecondaryButton type="button">
                                    Cancelar
                                </SecondaryButton>
                            </Link>
                            <PrimaryButton type="submit" disabled={processing}>
                                {processing ? 'Creando...' : 'Crear Usuario'}
                            </PrimaryButton>
                        </div>
                    </form>
                </div>
            </div>
        </StaffLayout>
    );
};

const Create: React.FC<CreateProps> = (props) => {
    return (
        <ThemeWrapper>
            <CreateContent {...props} />
        </ThemeWrapper>
    );
};

export default Create;
